class VideoAnalytics {
    /**
     * Initializes the analytics module with dual GA4 sending capabilities.
     * @param {string} [clientMeasurementId] - The client's GA4 Measurement ID (optional).
     * @param {object} [options={}] - Configuration options like 'enabled' and 'debug'.
     * @param {string} [apiKey] - The widget's API key to identify the client.
     */
    constructor(clientMeasurementId, options = {}, apiKey) {
        // Client GA4 configuration
        this.clientMeasurementId = this.validateGA4Id(clientMeasurementId);

        // Your internal GA4 configuration
        this.internalMeasurementId = this.validateGA4Id('G-YMYX0XZ7DY');

        this.enabled = options.enabled !== false;
        this.debug = options.debug || false;
        this.customDimensions = options.customDimensions || {};

        // API Key to identify the client in your analytics
        this.apiKey = apiKey;

        // Configuration status
        this.clientConfigured = false;
        this.internalConfigured = false;
        this.eventQueue = [];

        this.sessionData = {
            sessionId: this.generateSessionId(),
            startTime: Date.now(),
            videosWatched: new Set(),
            storiesViewed: new Set(),
            clientId: this.apiKey || 'unknown_client'
        };

        this.currentVideo = {
            id: null,
            title: null,
            description: null,
            startTime: null,
            watchTime: 0,
            progress: 0,
            completed: false,
            interactions: []
        };

        this.init();
    }

    init() {
        if (!this.enabled) {
            if (this.debug) console.log("[Analytics] Analytics is globally disabled.");
            return;
        }

        this.tryConfigure();
    }

    tryConfigure() {
        if (typeof window.gtag !== 'function') {
            if (this.debug) console.warn('[Analytics] gtag function not found yet. Configuration deferred.');
            return false;
        }

        // Configure Client's GA4
        if (this.clientMeasurementId && !this.clientConfigured) {
            window.gtag('config', this.clientMeasurementId);
            this.clientConfigured = true;
            if (this.debug) {
                console.log(`[Analytics] Client GA4 configured: ${this.clientMeasurementId}`);
            }
        }

        // Configure Your Internal GA4
        if (this.internalMeasurementId && !this.internalConfigured) {
            window.gtag('config', this.internalMeasurementId);
            this.internalConfigured = true;
            if (this.debug) {
                console.log(`[Analytics] Internal GA4 configured: ${this.internalMeasurementId}`);
            }
        }

        // Process queued events if any
        if (this.eventQueue.length > 0) {
            if (this.debug) console.log(`[Analytics] Processing ${this.eventQueue.length} queued events.`);
            const queue = [...this.eventQueue];
            this.eventQueue = [];
            queue.forEach(item => this.sendEvent(item.eventName, item.parameters));
        }

        return true;
    }
    validateGA4Id(measurementId) {
        if (!measurementId) return null;

        const ga4Pattern = /^G-[A-Z0-9]{10}$/;

        if (!ga4Pattern.test(measurementId)) {
            if (this.debug) {
                console.warn(`[Analytics] Invalid GA4 Measurement ID format: ${measurementId}`);
            }
            return null;
        }

        return measurementId;
    }
    generateSessionId() {
        return `vs_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    }
    sendEvent(eventName, parameters) {
        if (!this.enabled) return;

        // Try to configure if not already done
        if (!this.clientConfigured || !this.internalConfigured) {
            const success = this.tryConfigure();
            if (!success) {
                if (this.debug) console.log(`[Analytics] gtag not available. Queuing event: ${eventName}`);
                this.eventQueue.push({ eventName, parameters });
                return;
            }
        }

        // Base parameters for all events
        const baseParameters = {
            ...parameters,
            ...this.customDimensions
        };

        // 1. Send to Client's GA4 (if configured)
        if (this.clientMeasurementId && typeof window.gtag === 'function') {
            try {
                window.gtag('event', eventName, {
                    ...baseParameters,
                    send_to: this.clientMeasurementId
                });
                if (this.debug) {
                    console.log(`[Analytics] Event "${eventName}" sent to Client GA4:`, baseParameters);
                }
            } catch (error) {
                if (this.debug) {
                    console.error('[Analytics] Error sending event to Client GA4:', error);
                }
            }
        }

        // 2. Send to Your Internal GA4 (with additional client identification)
        if (this.internalMeasurementId && typeof window.gtag === 'function') {
            const internalParameters = {
                ...baseParameters,
                // Add client identification parameters
                client_id: this.sessionData.clientId,
                client_api_key: this.apiKey,
                client_domain: window.location.hostname,
                session_id: this.sessionData.sessionId,
                client_url: window.location.href,
                client_referrer: document.referrer || 'direct'
            };

            try {
                window.gtag('event', eventName, {
                    ...internalParameters,
                    send_to: this.internalMeasurementId
                });
                if (this.debug) {
                    console.log(`[Analytics] Event "${eventName}" sent to Internal GA4:`, internalParameters);
                }
            } catch (error) {
                if (this.debug) {
                    console.error('[Analytics] Error sending event to Internal GA4:', error);
                }
            }
        }
    }
    trackVideoStart(videoData) {
        const eventData = {
            video_id: videoData.id,
            video_title: videoData.title || videoData.name,
            video_description: videoData.description,
            story_id: videoData.storyId,
            video_position: videoData.position || 0,
            video_duration: videoData.duration || 0,
            event_timestamp: Date.now()
        };

        this.currentVideo = {
            id: videoData.id,
            title: videoData.title || videoData.name,
            description: videoData.description,
            startTime: Date.now(),
            watchTime: 0,
            progress: 0,
            completed: false,
            interactions: []
        };

        this.sendEvent('video_start', eventData);
        this.sessionData.videosWatched.add(videoData.id);
    }
    trackVideoProgress(progressData) {
        if (!this.currentVideo.id) return;

        const progress = Math.round(progressData.progress || 0);
        this.currentVideo.progress = progress;
        this.currentVideo.watchTime = Date.now() - this.currentVideo.startTime;

        const milestones = [25, 50, 75, 100];
        const currentMilestone = milestones.find(m =>
            progress >= m && !this.currentVideo.interactions.includes(`progress_${m}`)
        );

        if (currentMilestone) {
            this.currentVideo.interactions.push(`progress_${currentMilestone}`);
            const eventData = {
                video_id: this.currentVideo.id,
                video_title: this.currentVideo.title,
                progress_percentage: currentMilestone,
                watch_time_ms: this.currentVideo.watchTime,
                current_time_s: Math.round(progressData.currentTime || 0),
                video_duration: progressData.duration || 0,
                event_timestamp: Date.now()
            };
            this.sendEvent('video_progress', eventData);
        }
    }
    trackVideoEnd(endData) {
        if (!this.currentVideo.id) return;

        const totalWatchTime = Date.now() - this.currentVideo.startTime;
        const completed = endData.completed || this.currentVideo.progress >= 95;

        const eventData = {
            video_id: this.currentVideo.id,
            video_title: this.currentVideo.title,
            total_watch_time_ms: totalWatchTime,
            completion_rate: this.currentVideo.progress,
            completed: completed,
            interactions_count: this.currentVideo.interactions.length,
            video_duration: endData.duration || 0,
            event_timestamp: Date.now()
        };

        this.sendEvent('video_end', eventData);

        if (completed) {
            this.sendEvent('video_complete', eventData);
        }
        // Reset current video state
        this.currentVideo = {
            id: null,
            title: null,
            description: null,
            startTime: null,
            watchTime: 0,
            progress: 0,
            completed: false,
            interactions: []
        };
    }
    trackStoryChange(storyData) {
        const eventData = {
            story_id: storyData.id,
            story_title: storyData.name || storyData.title,
            story_description: storyData.description,
            videos_count: storyData.videos?.length || 0,
            event_timestamp: Date.now()
        };
        this.sendEvent('story_view', eventData);
        this.sessionData.storiesViewed.add(storyData.id);
    }
    trackPlayerOpen() {
        const eventData = {
            action: 'player_open',
            event_timestamp: Date.now(),
            session_duration_ms: Date.now() - this.sessionData.startTime
        };
        this.sendEvent('player_interaction', eventData);
    }
    trackPlayerClose() {
        const sessionDuration = Date.now() - this.sessionData.startTime;
        const eventData = {
            action: 'player_close',
            session_duration_ms: sessionDuration,
            videos_watched_count: this.sessionData.videosWatched.size,
            stories_viewed_count: this.sessionData.storiesViewed.size,
            event_timestamp: Date.now()
        };
        this.sendEvent('player_interaction', eventData);
        this.sendEvent('session_end', eventData);
    }
    trackVideoInteraction(interactionType, data = {}) {
        const eventData = {
            video_id: this.currentVideo.id,
            video_title: this.currentVideo.title,
            interaction_type: interactionType,
            event_timestamp: Date.now(),
            video_current_time: data.currentTime || 0,
            video_progress: this.currentVideo.progress,
            ...data
        };
        this.sendEvent('video_interaction', eventData);
        this.currentVideo.interactions.push(interactionType);
    }
    trackNavigation(direction, from, to) {
        const eventData = {
            navigation_direction: direction,
            from_video_id: from,
            to_video_id: to,
            event_timestamp: Date.now(),
            session_duration_ms: Date.now() - this.sessionData.startTime
        };
        this.sendEvent('video_navigation', eventData);
    }
    trackError(errorType, errorMessage, errorData = {}) {
        const eventData = {
            error_type: errorType,
            error_message: errorMessage,
            video_id: this.currentVideo.id,
            video_title: this.currentVideo.title,
            event_timestamp: Date.now(),
            ...errorData
        };
        this.sendEvent('widget_error', eventData);
    }
    trackWidgetLoad(loadData = {}) {
        const eventData = {
            action: 'widget_load',
            stories_count: loadData.storiesCount || 0,
            load_time_ms: loadData.loadTime || 0,
            widget_position: loadData.position || 'unknown',
            widget_theme: loadData.theme || 'default',
            event_timestamp: Date.now()
        };
        this.sendEvent('widget_lifecycle', eventData);
    }
    trackWidgetDestroy() {
        const sessionDuration = Date.now() - this.sessionData.startTime;
        const eventData = {
            action: 'widget_destroy',
            session_duration_ms: sessionDuration,
            videos_watched_count: this.sessionData.videosWatched.size,
            stories_viewed_count: this.sessionData.storiesViewed.size,
            total_interactions: this.currentVideo.interactions.length,
            event_timestamp: Date.now()
        };
        this.sendEvent('widget_lifecycle', eventData);
    }
    setCustomDimension(key, value) {
        this.customDimensions[key] = value;
        if (this.debug) {
            console.log(`[Analytics] Custom dimension set: ${key} = ${value}`);
        }
    }
    setClientMetadata(metadata) {
        this.customDimensions = {
            ...this.customDimensions,
            client_plan: metadata.plan || 'unknown',
            client_tier: metadata.tier || 'free',
            client_region: metadata.region || 'unknown',
            client_industry: metadata.industry || 'unknown'
        };
    }
    enable() {
        this.enabled = true;
        if (this.debug) {
            console.log('[Analytics] Analytics enabled');
        }
    }
    disable() {
        this.enabled = false;
        if (this.debug) {
            console.log('[Analytics] Analytics disabled');
        }
    }
    getSessionSummary() {
        return {
            sessionId: this.sessionData.sessionId,
            clientId: this.sessionData.clientId,
            duration: Date.now() - this.sessionData.startTime,
            videosWatched: Array.from(this.sessionData.videosWatched),
            videosWatchedCount: this.sessionData.videosWatched.size,
            storiesViewed: Array.from(this.sessionData.storiesViewed),
            storiesViewedCount: this.sessionData.storiesViewed.size,
            currentVideo: this.currentVideo,
            customDimensions: this.customDimensions,
            analyticsEnabled: this.enabled,
            clientGA4: this.clientMeasurementId,
            internalGA4: this.internalMeasurementId
        };
    }
    getClientAnalytics() {
        return {
            clientId: this.sessionData.clientId,
            apiKey: this.apiKey,
            domain: window.location.hostname,
            url: window.location.href,
            referrer: document.referrer || 'direct',
            userAgent: navigator.userAgent,
            timestamp: Date.now()
        };
    }
    // Method to manually send a custom event
    sendCustomEvent(eventName, customParameters = {}) {
        if (!this.enabled) {
            if (this.debug) {
                console.warn(`[Analytics] Cannot send custom event "${eventName}" - analytics disabled`);
            }
            return;
        }

        const eventData = {
            custom_event: true,
            event_timestamp: Date.now(),
            ...customParameters
        };

        this.sendEvent(eventName, eventData);
    }
    getDebugInfo() {
        return {
            enabled: this.enabled,
            debug: this.debug,
            clientMeasurementId: this.clientMeasurementId,
            internalMeasurementId: this.internalMeasurementId,
            sendToClientGA4: this.sendToClientGA4,
            sendToInternalGA4: this.sendToInternalGA4,
            apiKey: this.apiKey,
            sessionData: this.sessionData,
            currentVideo: this.currentVideo,
            customDimensions: this.customDimensions,
            gtagAvailable: typeof window.gtag === 'function'
        };
    }
}

export default VideoAnalytics;